import React, { useState, useEffect, useRef } from 'react';
import { Terminal, X, Copy, Trash2 } from 'lucide-react';
import { Button } from './ui/button';

const AdminDevTools = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [logs, setLogs] = useState(() => {
    const savedLogs = localStorage.getItem('admin_console_logs');
    return savedLogs ? JSON.parse(savedLogs) : [];
  });
  const logsEndRef = useRef(null);

  // Check if admin is logged in
  useEffect(() => {
    const checkAdmin = () => {
      const isAdmin = localStorage.getItem('adminAuth') === 'true';
      setIsVisible(isAdmin);
    };

    checkAdmin();
    const interval = setInterval(checkAdmin, 2000);
    return () => clearInterval(interval);
  }, []);

  // Capture console logs
  useEffect(() => {
    if (!isVisible) return;

    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;

    const addLog = (type, args) => {
      const timestamp = new Date().toLocaleTimeString();
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      
      setLogs(prev => {
        const newLogs = [...prev.slice(-99), { type, message, timestamp }];
        localStorage.setItem('admin_console_logs', JSON.stringify(newLogs));
        return newLogs;
      });
    };

    console.log = (...args) => {
      originalLog.apply(console, args);
      addLog('log', args);
    };

    console.error = (...args) => {
      originalError.apply(console, args);
      addLog('error', args);
    };

    console.warn = (...args) => {
      originalWarn.apply(console, args);
      addLog('warn', args);
    };

    return () => {
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
    };
  }, [isVisible]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const clearLogs = () => {
    setLogs([]);
    localStorage.removeItem('admin_console_logs');
  };

  const copyLogs = () => {
    const logText = logs.map(log => `[${log.timestamp}] ${log.type.toUpperCase()}: ${log.message}`).join('\n');
    navigator.clipboard.writeText(logText);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating Dev Tools Button */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="fixed bottom-4 right-4 z-[9999] bg-gradient-to-r from-purple-600 to-blue-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110"
          title="Open Dev Tools"
        >
          <Terminal className="w-5 h-5" />
        </button>
      )}

      {/* Dev Tools Panel */}
      {isExpanded && (
        <div className="fixed bottom-0 right-0 w-full md:w-2/3 lg:w-1/2 h-96 bg-gray-900 text-white shadow-2xl z-[9999] flex flex-col border-t-4 border-purple-500">
          {/* Header */}
          <div className="flex items-center justify-between p-3 bg-gray-800 border-b border-gray-700">
            <div className="flex items-center gap-2">
              <Terminal className="w-5 h-5 text-purple-400" />
              <span className="font-bold text-sm">Admin Dev Console</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={copyLogs}
                className="text-xs text-gray-300 hover:text-white"
              >
                <Copy className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearLogs}
                className="text-xs text-gray-300 hover:text-white"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Logs Area */}
          <div className="flex-1 overflow-y-auto p-3 space-y-1 font-mono text-xs">
            {logs.length === 0 ? (
              <div className="text-gray-500 text-center py-8">No logs yet...</div>
            ) : (
              logs.map((log, index) => (
                <div
                  key={index}
                  className={`p-2 rounded ${
                    log.type === 'error'
                      ? 'bg-red-900/20 text-red-300'
                      : log.type === 'warn'
                      ? 'bg-yellow-900/20 text-yellow-300'
                      : 'bg-gray-800 text-gray-300'
                  }`}
                >
                  <div className="flex gap-2">
                    <span className="text-gray-500">[{log.timestamp}]</span>
                    <span className={`font-bold ${
                      log.type === 'error' ? 'text-red-400' :
                      log.type === 'warn' ? 'text-yellow-400' :
                      'text-blue-400'
                    }`}>
                      {log.type.toUpperCase()}:
                    </span>
                  </div>
                  <div className="ml-20 mt-1 whitespace-pre-wrap break-all">
                    {log.message}
                  </div>
                </div>
              ))
            )}
            <div ref={logsEndRef} />
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDevTools;
